const JOURNEY_STORAGE_KEY = "ccg_funnel_journey_id";
const ATTRIBUTION_STORAGE_KEY = "ccg_funnel_attribution";
const AJP_JOURNEY_STORAGE_KEY = "ajp_origin_journey_id";

const uuid = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

export const inferFunnelBrand = () => {
  if (typeof window === "undefined") return "ccg";
  const host = window.location.hostname.toLowerCase();
  const path = window.location.pathname.toLowerCase();
  if (host.includes("minibusgrab") || path.includes("minibus")) return "minibusgrab";
  if (host.includes("cheapcoachgrab") || path.includes("coach")) return "cheapcoachgrab";
  return "ccg";
};

const normaliseFunnelBrand = (brand) => {
  if (brand === "coachgrab") return "cheapcoachgrab";
  if (["ccg", "minibusgrab", "cheapcoachgrab", "planner"].includes(brand)) return brand;
  return inferFunnelBrand();
};

export const getFunnelJourneyId = () => {
  if (typeof window === "undefined") return null;
  try {
    let journeyId = window.sessionStorage.getItem(JOURNEY_STORAGE_KEY);
    if (!journeyId) {
      journeyId = uuid();
      window.sessionStorage.setItem(JOURNEY_STORAGE_KEY, journeyId);
    }
    return journeyId;
  } catch {
    return uuid();
  }
};

export const getAjpJourneyId = () => {
  if (typeof window === "undefined") return null;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("ajp_journey_id");
    if (fromUrl) {
      window.sessionStorage.setItem(AJP_JOURNEY_STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return window.sessionStorage.getItem(AJP_JOURNEY_STORAGE_KEY) || null;
  }
  catch { return null; }
};

const rememberAjpJourneyId = (journeyId) => {
  if (typeof window === "undefined" || !journeyId) return;
  try { window.sessionStorage.setItem(AJP_JOURNEY_STORAGE_KEY, String(journeyId)); }
  catch { /* Tracking must never interrupt the customer flow. */ }
};

export const getAttribution = () => {
  if (typeof window === "undefined") return {};
  const allowed = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "gbraid", "wbraid"];
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = Object.fromEntries(allowed.map((key) => [key, params.get(key)]).filter(([, value]) => value));
    if (Object.keys(fromUrl).length) {
      window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(fromUrl));
      return fromUrl;
    }
    return JSON.parse(window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const endpoint = () => {
  if (typeof window === "undefined") return "";

  if (process.env.NODE_ENV === "development") {
    return `http://${window.location.hostname}:5000/api/funnel-events`;
  }

  const apiBase = process.env.REACT_APP_API_URL || "https://api.cheapcabgrab.com";
  return `${apiBase.replace(/\/$/, "")}/api/funnel-events`;
};

const backendPayload = (eventName, details) => {
  const anonymousVisitorId = getFunnelJourneyId();
  const suppressAjpJourneyId = details.suppress_ajp_journey_id === true;
  const explicitAjpJourneyId = details.ajp_journey_id || null;
  if (explicitAjpJourneyId) rememberAjpJourneyId(explicitAjpJourneyId);
  const ajpJourneyId = suppressAjpJourneyId ? null : explicitAjpJourneyId || getAjpJourneyId();
  const coreKeys = new Set([
    "brand", "quote_id", "quoteId", "vehicle_code", "vehicleCode", "cta_type", "ctaType",
    "fare", "fare_gbp", "step", "step_name", "step_number", "field_name", "status",
    "suppress_ajp_journey_id",
  ]);
  const metadata = Object.fromEntries(
    Object.entries({ ...getAttribution(), ...details }).filter(
      ([key, value]) => !coreKeys.has(key) && value !== undefined && value !== null && value !== ""
    )
  );

  return {
    event_id: uuid(),
    event_name: eventName,
    brand: normaliseFunnelBrand(details.brand),
    journey_id: ajpJourneyId || anonymousVisitorId,
    quote_id: details.quote_id || details.quoteId || undefined,
    page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
    vehicle_code: details.vehicle_code || details.vehicleCode || undefined,
    cta_type: details.cta_type || details.ctaType || details.action || undefined,
    fare_gbp: details.fare_gbp ?? details.fare,
    step_name: details.step_name || details.step || details.field_name || undefined,
    step_number: details.step_number,
    status: details.status,
    occurred_at: new Date().toISOString(),
    metadata: {
      ...metadata,
      anonymous_visitor_id: anonymousVisitorId,
      ajp_journey_id: ajpJourneyId || undefined,
      referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
      device_type: typeof window !== "undefined" && window.matchMedia?.("(max-width: 767px)").matches ? "mobile" : "desktop",
      viewport_width: typeof window !== "undefined" ? window.innerWidth : undefined,
      viewport_height: typeof window !== "undefined" ? window.innerHeight : undefined,
    },
  };
};

const sendToBackend = (payload, keepalive = false) => {
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  window.fetch(endpoint(), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive,
  }).catch(() => {});
};

export const trackFunnelEvent = (eventName, details = {}, options = {}) => {
  if (typeof window === "undefined" || !eventName) return;
  const payload = Object.fromEntries(
    Object.entries(details).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );

  if (typeof window.gtag === "function") window.gtag("event", eventName, payload);
  else {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...payload });
  }

  sendToBackend(backendPayload(eventName, payload), options.keepalive !== false);
};

const AJP_PLAN_GOOGLE_ADS_SEND_TO = "AW-18241240938/kE7fCLiXha0dEOr-jPpD";

// Record the Google Ads plan-generation conversion only after the planner API
// has returned a complete plan. The journey id also lets Google deduplicate a
// repeated render or retry of the same generated plan.
export const trackAjpPlanGoogleAdsConversion = (journeyId) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", "conversion", {
    send_to: AJP_PLAN_GOOGLE_ADS_SEND_TO,
    transaction_id: journeyId || undefined,
  });
};

// React development Strict Mode remounts effects once. Suppress only identical
// impression events occurring in the same short render window; genuine revisits
// later in the session are still recorded.
export const trackFunnelEventOnce = (eventName, details = {}, windowMs = 1500) => {
  if (typeof window === "undefined" || !eventName) return;
  const identity = [eventName, window.location.pathname, details.brand, details.tracking_source, details.airport, details.direction, details.placement, details.cta_label].filter(Boolean).join(":");
  const key = `ccg_funnel_once:${identity}`;
  const now = Date.now();
  try {
    const previous = Number(window.sessionStorage.getItem(key) || 0);
    if (previous && now - previous < windowMs) return;
    window.sessionStorage.setItem(key, String(now));
  } catch { /* Tracking still proceeds when storage is unavailable. */ }
  trackFunnelEvent(eventName, details);
};

export const trackFunnelExit = (eventName, details = {}) => {
  trackFunnelEvent(eventName, details, { keepalive: true });
};
